import isEmpty from 'lodash/isEmpty';

import type { PayloadAction } from '@reduxjs/toolkit';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { makeSeoPayload, preparePath, prepareSeoData, TPageType } from '../../helpers/seo';
import type { IMetadata } from '../../interfaces/metadata';
import { TMetadataParams } from '../../server/services/metadata';
import { loadMetadata } from '../../services/metadata';
import { ALL_ROUTER_QUERIES } from '../../types/router';
import { GlobalState } from '../index';
import { getMetadata } from '../selectors/metadata';

export type IState = {
  data: IMetadata | Record<string, any>;
};

const initialState: IState = {
  data: {},
};

const name = 'metadata';

const metadataSlice = createSlice({
  name,
  initialState,
  reducers: {
    setMetadata: (state, { payload }: PayloadAction<{ data: IMetadata; pathname: string }>) => {
      state.data[payload.pathname] = payload.data;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchSeo.fulfilled, (state, { payload }) => {
      state.data[payload.pathname] = payload.data;
    });
  },
});

const { actions, reducer } = metadataSlice;

export const { setMetadata } = actions;

export const fetchSeo = createAsyncThunk<
  { data: IMetadata; pathname: string },
  { params: TMetadataParams; pageType: TPageType },
  { state: GlobalState }
>(`${name}/fetchSeo`, async ({ params, pageType }, { getState }) => {
  const pathname = params.url;
  const seo = await loadMetadata(params);

  const data = prepareSeoData(seo, pageType, getState(), params.organization?.alias);

  return { data, pathname };
});

export const fetchSeoIfNeeded = (query: ALL_ROUTER_QUERIES, pathname: string, pageType: TPageType) => {
  return async (dispatch, getState) => {
    pathname = preparePath(pathname);

    const seo = getMetadata(pathname)(getState());

    if (isEmpty(seo)) {
      const payload = makeSeoPayload(query, pathname, getState());
      return await dispatch(fetchSeo({ params: payload, pageType }));
    }
  };
};

export default reducer;
